import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import TitleAndBack from 'components/v2/TitleAndBack';
import { RouteComponentProps, Redirect } from 'react-router-dom';
import EpdRoutes, { EpdLinks } from 'routes/EpdRoutes';
import { toaster } from 'components/v2/toast';
import { ACCESS_DENIED_DETAIL } from 'util/epdActionErrorUtils';
import { getApiErrorDetail, getApiErrorStatus } from 'util/apiErrorUtils';
import { getPortalSchema, getPropertiesSchema } from 'services/api/dynamicFormsApi';
import { getVisibleEpdCopy } from 'services/api/epdApi';
import { QUERY_KEYS } from 'services/api/constants';
import { Container } from 'styles/Styles.styled';
import { WizardHeaderGrid } from 'styles/v2/Styles.styled';
import DynamicContentRenderer from './DynamicContentRenderer';
import { withGreyBackground } from 'pages/v2/WithGreyBackground';
import createDocumentStore from './store/createDocumentStore';
import { StoreContext } from './hooks/useDocumentStore';
import DataSyncIndicator from './components/DataSyncIndicator';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { documentDataSourcesQueryOptions, documentQueryOptions, retryUnlessClientError } from './documentQueries';

interface Params {
  docId: string;
}

const EPD_NOT_FOUND_DETAIL = 'Could not find the EPD.';

const DynamicForm: React.FC<RouteComponentProps<Params>> = ({
  match: {
    params: { docId },
  },
  history,
}) => {
  const docQuery = useQuery(documentQueryOptions(docId));
  const schemaId = docQuery.data?.schemaId;
  // A Hidden document answers 404; its EPD may still be visible as a Simple Wizard or Compiler copy (R12, AC13).
  const isDocumentNotFound = docQuery.isError && getApiErrorStatus(docQuery.error) === 404;
  const visibleCopyQuery = useQuery({
    queryKey: [QUERY_KEYS.EPD_VISIBLE_COPY, docId],
    queryFn: () => getVisibleEpdCopy(docId),
    enabled: isDocumentNotFound,
    staleTime: 0,
    refetchOnWindowFocus: false,
    retry: retryUnlessClientError,
  });

  const propertiesSchemaQuery = useQuery({
    queryKey: ['DOC_SCHEMA', 'PROPERTIES', schemaId],
    queryFn: () => getPropertiesSchema(schemaId!),
    enabled: !!docQuery.data,
    throwOnError: true,
  });
  const portalSchemaQuery = useQuery({
    queryKey: ['DOC_SCHEMA', 'PORTAL', schemaId],
    queryFn: () => getPortalSchema(schemaId!),
    enabled: !!docQuery.data,
    throwOnError: true,
  });
  const dataSourcesQuery = useQuery(documentDataSourcesQueryOptions(docId));

  const storeDeps = [docQuery.data, propertiesSchemaQuery.data, portalSchemaQuery.data, dataSourcesQuery.data];
  const store = useMemo(() => {
    const allLoaded = storeDeps.every(Boolean);
    if (!allLoaded) {
      return null;
    }

    return createDocumentStore({
      docId,
      initialData: docQuery.data!.data,
      initialSchemaId: docQuery.data!.schemaId,
      initialDataSources: dataSourcesQuery.data,
      state: docQuery.data!.state,
      metadata: docQuery.data!.metadata,
      properties: propertiesSchemaQuery.data,
    });
  }, storeDeps);

  if (docQuery.isError && getApiErrorStatus(docQuery.error) === 403) {
    toaster({
      severity: 'error',
      summary: 'Access denied',
      details: ACCESS_DENIED_DETAIL,
      // Stable id so a re-render / StrictMode double-invoke does not stack duplicate toasts.
      options: { toastId: 'dynamic-forms-access-denied' },
    });
    return <Redirect to={EpdRoutes.dashboard} />;
  }

  // Wait for this mount's answer (the loading header below shows meanwhile): a cached one may name a copy
  // that has been switched since.
  if (isDocumentNotFound && visibleCopyQuery.isFetchedAfterMount) {
    const visibleCopy = visibleCopyQuery.data;
    // The two copies of a migrated EPD may have different ids - always follow the id the server returned.
    if (!visibleCopyQuery.isError && visibleCopy && (visibleCopy.format === 'wizard' || visibleCopy.format === 'compiler')) {
      return <Redirect to={EpdLinks.epd(visibleCopy.id)} />;
    }

    // No visible copy, or the visible copy is this Dynamic Forms document yet it cannot be loaded: nothing to redirect to.
    toaster({
      severity: 'error',
      summary: 'EPD not found',
      details: getApiErrorDetail(docQuery.error) ?? EPD_NOT_FOUND_DETAIL,
      // Stable id so a re-render / StrictMode double-invoke does not stack duplicate toasts.
      options: { toastId: 'dynamic-forms-not-found' },
    });
    return <Redirect to={EpdRoutes.dashboard} />;
  }

  if (!store) {
    return (
      <Container>
        <WizardHeaderGrid>
          <TitleAndBack title={'Loading...'} backTo={'Back to the dashboard'} onBack={() => history.push(EpdRoutes.dashboard)} />
        </WizardHeaderGrid>
      </Container>
    );
  }

  return (
    <StoreContext.Provider value={store}>
      <DataSyncIndicator />
      <DynamicContentRenderer schema={portalSchemaQuery.data!} />

      {/* Dummy dialog so that 'confirmDialog' function can be called somewhere inside the tree */}
      <ConfirmDialog />
    </StoreContext.Provider>
  );
};

export default withGreyBackground(DynamicForm);
